import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CAlert, CButton, CCard, CCardBody, CCardHeader, CSpinner, CStepper } from "@coreui/react-pro";
import { useAddOns, useCancelAddOn } from "../../api/hooks/useAddOns";
import type { ApiError } from "../../api/client";
import { useFailure } from "../../context/useFailure";
import { useToast } from "../../context/useToast";
import { addOnDetailsPath, routePaths } from "../../routes/route-paths";
import { formatDateShort } from "../../lib/dates";

// CStepper's exposed imperative API. Mirrors the membership End wizard so the two flows stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

export function AddOnEndWizard() {
  const navigate = useNavigate();
  const { addOnId = "" } = useParams<{ addOnId: string }>();

  const addOns = useAddOns();
  const endRemote = useCancelAddOn();
  const { showSuccess } = useToast();
  const { showFailure, clearFailure } = useFailure();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Only one bought here and still running can be ended here — the same rule the server enforces.
  const addOn = addOns.data?.find(
    (candidate) => candidate.id === addOnId && candidate.held?.boughtHere && candidate.held.activeTo === null,
  );

  if (addOns.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <CSpinner color="primary" />
      </div>
    );
  }

  if (addOns.error) {
    return (
      <div>
        <CAlert color="danger">Unable to load your add-on. Please try again.</CAlert>
      </div>
    );
  }

  if (!addOn) {
    return (
      <div>
        <CAlert color="warning">
          No active add-on you can end here. <Link to={routePaths.addOns}>Back to add-ons</Link>.
        </CAlert>
      </div>
    );
  }

  const name = addOn.name;
  const detailPath = addOnDetailsPath(addOnId);

  const handleNext = () => stepperRef.current?.next();

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(detailPath);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      const ended = await endRemote.mutateAsync(addOnId);
      showSuccess(`${name} ends on ${formatDateShort(ended.activeTo)}.`);
      navigate(detailPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to end this add-on. Please try again.";
      showFailure(message, (err as ApiError)?.traceId);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Details",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Details</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              You're about to end the following add-on. Review the details, then continue to confirm.
            </p>
            <dl className="row mb-4">
              <dt className="col-bo-3 text-body-secondary fw-normal">Add-on</dt>
              <dd className="col-bo-9 fw-semibold">{name}</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Status</dt>
              <dd className="col-bo-9">Active</dd>
              <dt className="col-bo-3 text-body-secondary fw-normal">Billing</dt>
              <dd className="col-bo-9">
                <Link to={routePaths.billing}>See Billing</Link>
              </dd>
            </dl>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="warning">
              Your <strong>{name}</strong> add-on will end once the period you have already paid for runs out.
            </CAlert>
            <p>When you confirm:</p>
            <ul className="mb-4">
              <li>
                Your add-on is marked as <strong>Ending</strong>, and stays yours to the end of the period you have
                already paid for — not to today.
              </li>
              <li>Billing stops. You will not be charged for it again.</li>
              <li>You can resume any time before it ends. After that, you can buy it again from Add-ons.</li>
            </ul>
            <hr className="mt-0 mb-4" />
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack} disabled={submitting}>
                Back
              </CButton>
              <CButton color="danger" onClick={handleSubmit} disabled={submitting}>
                {submitting && <CSpinner size="sm" className="me-2" />}
                End add-on
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>End add-on</h2>
      <p className="text-body-secondary">
        End your <strong>{name}</strong> add-on.
      </p>
      <hr />

      <CStepper ref={stepperRef} steps={steps} onStepChange={setCurrentStep} onReset={() => setCurrentStep(1)} />
    </>
  );
}

export default AddOnEndWizard;
